import { Injectable, inject, signal } from '@angular/core';
import { NebulaNavigationService } from 'nebula-ui-kit';
import { SidebarService } from './sidebar.service';
import { SidebarMenuItem } from '../../../domain/src/lib/sidebar.model';
import { getModuleFromRoute } from '../../../utils/src/lib/route-validator';
import { MAX_OPEN_TABS } from '../../../const/src/lib/layout.constants';

/** Opcion del menu aplanada, indexable para la busqueda global del header. */
export interface MenuSearchOption {
  label: string;
  route: string;
  icon: string;
  group: string;
  relevance?: number;
}

/** Resultado de abrir una opcion desde la busqueda global. */
export type OpenResult = 'opened' | 'activated' | 'limit';

/**
 * Busqueda global del header (estilo command-palette de Electo): filtra las OPCIONES del menu
 * a las que el usuario tiene acceso por rol y, al elegir una, la abre como pestaña reutilizando
 * el mismo mecanismo del sidebar ({@link NebulaNavigationService#addTabFromMenu}) y respetando el
 * limite de pestañas por modulo ({@link MAX_OPEN_TABS}). Es 100% cliente: no consulta backend.
 */
@Injectable({ providedIn: 'root' })
export class GlobalMenuSearchService {
  private readonly sidebarService = inject(SidebarService);
  private readonly navService = inject(NebulaNavigationService);

  /** Cache de opciones planas del menu del usuario (se carga al primer uso). */
  private readonly options = signal<MenuSearchOption[]>([]);
  private loaded = false;

  /** Carga (una vez) y cachea las opciones planas del menu del usuario. */
  loadOptions(): void {
    if (this.loaded) return;
    this.loaded = true;
    this.sidebarService.getMenu().subscribe({
      next: (tree) => queueMicrotask(() => this.options.set(this.flatten(tree))),
      error: () => {
        this.loaded = false;
      },
    });
  }

  /** Aplana el arbol del menu a hojas navegables, conservando el grupo como categoria. */
  private flatten(tree: SidebarMenuItem[]): MenuSearchOption[] {
    const out: MenuSearchOption[] = [];
    (tree || []).forEach((group) => {
      if (group.children && group.children.length > 0) {
        group.children.forEach((opt) => {
          if (opt.route) {
            out.push({ label: opt.label, route: opt.route, icon: opt.icon, group: group.label });
          }
        });
      } else if (group.route) {
        out.push({ label: group.label, route: group.route, icon: group.icon, group: '' });
      }
    });
    return out;
  }

  /** Busca opciones del menu por relevancia (titulo y categoria). Devuelve el top 15. */
  search(query: string): MenuSearchOption[] {
    const q = this.normalize(query);
    if (q.length < 2) return [];
    return this.options()
      .map((o) => ({ ...o, relevance: this.relevance(o, q) }))
      .filter((o) => (o.relevance ?? 0) > 0)
      .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))
      .slice(0, 15);
  }

  private relevance(o: MenuSearchOption, q: string): number {
    const title = this.normalize(o.label);
    const group = this.normalize(o.group);
    let score = 0;
    if (title === q) score += 100;
    if (title.startsWith(q)) score += 50;
    if (title.includes(q)) score += 30;
    if (group.includes(q)) score += 20;
    return score;
  }

  /**
   * Abre la opcion como pestaña: si ya esta abierta la activa; si no, valida el limite por modulo
   * y la agrega via {@link NebulaNavigationService#addTabFromMenu} (que ademas navega).
   */
  openOption(opt: MenuSearchOption): OpenResult {
    // La ruta del menu puede venir relativa (p. ej. '/tipo-plan/list'); se normaliza a
    // '/layout/<modulo>/...' igual que el sidebar, o no navega (pestaña vacia).
    const route = this.normalizeRoute(opt.route);
    const key = this.routeKey(route);
    const existing = this.navService.tabs().find((t) => t.route && this.routeKey(t.route) === key);
    if (existing?.id) {
      this.navService.selectTab(existing.id);
      return 'activated';
    }

    const module = getModuleFromRoute(route);
    if (module) {
      const openInModule = this.navService
        .tabs()
        .filter(
          (t) =>
            t.route &&
            getModuleFromRoute(t.route) === module &&
            !this.isModuleBaseRoute(t.route, module),
        ).length;
      if (openInModule >= MAX_OPEN_TABS) {
        return 'limit';
      }
    }

    this.navService.addTabFromMenu({ label: opt.label, icon: opt.icon, route });
    return 'opened';
  }

  /**
   * Normaliza la ruta del menu a la ruta real del router ('/layout/<modulo>/...'), misma logica que
   * el sidebar ({@code normalizeRouteStatic}): dedup de '/layout/mod/mod/', colapso de slashes y
   * prefijo del modulo activo (localStorage 'selectedRoute').
   */
  private normalizeRoute(route: string): string {
    if (!route) return '';
    const modules = 'accounting|tesorería|tesoreria|budget|vault|maestros|invoicing';
    let n = route;
    n = n.replace(new RegExp(`/layout/(${modules})/\\1/`, 'g'), '/layout/$1/');
    n = n.replace(/\/+/g, '/');
    if (!n.startsWith('/')) n = '/' + n;

    const active = localStorage.getItem('selectedRoute');
    const valid = [
      'accounting',
      'tesorería',
      'tesoreria',
      'budget',
      'vault',
      'maestros',
      'invoicing',
    ];
    const target = valid.includes(active || '')
      ? active === 'tesoreria'
        ? 'tesorería'
        : active || 'accounting'
      : 'accounting';

    n = n.replace(new RegExp(`^/layout/(${modules})/`), `/layout/${target}/`);
    if (!n.match(/^\/layout\/(accounting|tesorería|budget|vault|maestros|invoicing)\//)) {
      const clean = n.replace(new RegExp(`^/(${modules})/`), '').replace(/^\//, '');
      n = `/layout/${target}/${clean}`;
    }
    return n;
  }

  /** Clave de ruta normalizada (sin query/hash, minusculas) para comparar pestañas. */
  private routeKey(route: string | null | undefined): string {
    if (!route) return '';
    return route
      .toLowerCase()
      .replace(/tesoreria/g, 'tesorería')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/$/, '');
  }

  /** True si la ruta es la base/dashboard del modulo (no cuenta para el limite). */
  private isModuleBaseRoute(route: string, module: string): boolean {
    const n = this.routeKey(route);
    return n === `/${module}` || n === `/layout/${module}`;
  }

  private normalize(str: string): string {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
